import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import AdminKampanjekoder from "./AdminKampanjekoder";
import AdminReferrals from "./AdminReferrals";
import AdminGrantTier from "./AdminGrantTier";

/**
 * /admin/kampanjer – felles side med to faner:
 *   - Koder (kampanje- + promo-koder)
 *   - Verving
 *
 * Fane-valget deles via ?tab= i URL slik at deep-linking fungerer.
 */
export default function AdminKampanjer() {
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Bakoverkompatibilitet: hvis noen fortsatt treffer /admin/verving eller
  // /admin/kampanjekoder → sett rett fane og bytt URL uten omdirigering.
  const initialTab = useMemo(() => {
    if (location.pathname.startsWith("/admin/verving") || location.pathname.startsWith("/admin/referrals")) {
      return "verving";
    }
    return sp.get("tab") || "koder";
  }, [location.pathname, sp]);

  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (location.pathname !== "/admin/kampanjer") {
      navigate(`/admin/kampanjer?tab=${initialTab}`, { replace: true });
    }
  }, [location.pathname, initialTab, navigate]);

  const changeTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(sp);
    next.set("tab", t);
    setSp(next, { replace: true });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-2">Kampanjer & verving</h1>
        <p className="text-[var(--cream-soft)] text-sm mb-6">Administrer kampanjekoder og verve-program på ett sted.</p>

        <div className="flex gap-2 border-b border-white/10 mb-6">
          {[
            ["koder",   "Koder"],
            ["gi-tilgang", "Gi VIP/Creator"],
            ["verving", "Verving"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => changeTab(k)}
              className={`px-4 py-2 font-meta text-xs uppercase tracking-widest border-b-2 transition ${
                tab === k
                  ? "border-[var(--magenta)] text-[var(--magenta)]"
                  : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
              }`}
              data-testid={`admin-kampanjer-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Vi rendrer eksisterende komponenter direkte – ingen duplisering av logikk.
            De har egen Layout, så vi tvinger dem inn i en "embedded"-modus ved å
            wrappe i div og vise deres eget innhold. Da AdminKampanjekoder/Referrals
            allerede bruker Layout, plasseres de bare her; dobbelt Layout er sjelden
            et problem i React, men vi setter dem inne i en innpakning. */}
        <div className="[&_[data-embedded-hide-layout]]:hidden" data-testid={`admin-kampanjer-pane-${tab}`}>
          {tab === "koder" && <AdminKampanjekoder embedded />}
          {tab === "gi-tilgang" && <AdminGrantTier />}
          {tab === "verving" && <AdminReferrals embedded />}
        </div>
      </div>
    </Layout>
  );
}
